import { ChartSvg, ChartTitle } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import { TextLines } from "@/components/tool-kit/diagrams/svg-parts";
import {
  type DrawnEdge,
  type DrawnLabel,
  LayeredArrows,
  LayeredLabels,
} from "@/components/tools/diagrams-more/layered-edges";
import { textColorOn } from "@/lib/tool-kit/charts/color";
import { LINE_HEIGHT } from "@/lib/tool-kit/charts/layout";
import { tint } from "@/lib/tool-kit/diagrams/color";
import {
  CONCEPT_FONT,
  type ConceptLayout,
  PHRASE_FONT,
} from "@/lib/tool-kit/diagrams-more/concept-map";
import { titledFrame } from "@/lib/tool-kit/diagrams-more/frame";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

/*
 * A concept map from `layoutConceptMap`. Concepts are rounded boxes; the first, at the
 * top of the map, is solid. Linking phrases sit on their arrows, so every arrow reads as
 * a sentence, and cross-links — links between branches already joined elsewhere — are
 * dashed.
 */

const BLUE = CHART_COLORS[0];

export function ConceptMapDiagram({
  id,
  layout,
  title,
  label,
  description,
}: {
  id: string;
  layout: ConceptLayout;
  title: string;
  label: string;
  description: string;
}) {
  const frame = titledFrame(layout.width, layout.height, title);
  const edges: DrawnEdge[] = layout.links.map((entry) => ({
    key: `link-${entry.path.index}`,
    points: entry.path.points,
    dashed: entry.link.crossLink,
  }));
  const labels: DrawnLabel[] = layout.links.flatMap((entry) =>
    entry.path.label
      ? [
          {
            key: `phrase-${entry.path.index}`,
            rect: entry.path.label,
            lines: entry.lines,
          },
        ]
      : []
  );
  const lineHeight = CONCEPT_FONT * LINE_HEIGHT;
  return (
    <ChartSvg
      description={description}
      height={frame.height}
      id={id}
      label={label}
      width={frame.width}
    >
      <ChartTitle title={frame.title} />
      <g transform={`translate(${px(frame.dx)} ${px(frame.dy)})`}>
        <LayeredArrows edges={edges} />
        {layout.concepts.map((entry, index) => {
          const solid = index === 0;
          const fill = solid ? BLUE : tint(BLUE, 0.88);
          return (
            <g key={entry.concept.id}>
              <rect
                fill={fill}
                height={px(entry.rect.height)}
                rx={10}
                stroke={BLUE}
                strokeWidth={1.75}
                width={px(entry.rect.width)}
                x={px(entry.rect.x)}
                y={px(entry.rect.y)}
              />
              <TextLines
                bold={solid}
                fill={solid ? textColorOn(BLUE) : CHART_INK.text}
                fontSize={CONCEPT_FONT}
                lineHeight={lineHeight}
                lines={entry.lines}
                top={
                  entry.rect.y +
                  (entry.rect.height - entry.lines.length * lineHeight) / 2
                }
                x={entry.rect.x + entry.rect.width / 2}
              />
            </g>
          );
        })}
        <LayeredLabels fontSize={PHRASE_FONT} labels={labels} />
      </g>
    </ChartSvg>
  );
}
